// src/components/AddEmployeePage.js

import { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { carryFrom } from '../utils/backTarget';
import { db } from '../firebase';
import { getEmployeeCredentialsPath } from '../services/firestorePaths';
import {
  ArrowLeft,
  User,
  FileText,
  ClipboardCheck,
  Plane,
  Stethoscope,
  HeartPulse,
  Award,
  UserPlus,
} from 'lucide-react';

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const FIELDS = [
  { key: 'NAME', label: 'Name', Icon: User },
  { key: 'CV_EXP', label: 'CV', Icon: FileText },
  { key: 'GCP_EXP', label: 'GCP', Icon: ClipboardCheck },
  { key: 'IATA_EXP', label: 'IATA', Icon: Plane },
  { key: 'ML_EXP', label: 'Medical License', Icon: Stethoscope },
  { key: 'CPR_EXP', label: 'CPR', Icon: HeartPulse },
  { key: 'CITI_EXP', label: 'CITI', Icon: Award },
];

const AddEmployeePage = () => {
  const navigate = useNavigate();
  // Back to Credentials keeps where Credentials was opened from (home or Dashboard).
  const location = useLocation();
  const listState = carryFrom(location.state);

  const [employee, setEmployee] = useState({
    NAME: '',
    CV_EXP: '',
    GCP_EXP: '',
    IATA_EXP: '',
    ML_EXP: '',
    CPR_EXP: '',
    CITI_EXP: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = async () => {
    if (!employee.NAME.trim()) {
      alert('Name is required.');
      return;
    }

    await addDoc(collection(db, getEmployeeCredentialsPath()), employee);
    alert('Employee added successfully.');
    navigate('/credentials', { state: listState });
  };

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative max-w-2xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        {/* Top bar */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => navigate('/credentials', { state: listState })}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl blur-md opacity-70" />
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-cyan-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-white" strokeWidth={2.4} />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                Staff
              </div>
              <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate">
                Add New Employee
              </h1>
            </div>
          </div>
        </div>

        {/* Form card */}
        <section className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-4 sm:p-5 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 opacity-60" />

          <div className="space-y-3.5">
            {FIELDS.map(({ key, label, Icon }) => (
              <div key={key}>
                <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                  <Icon className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                  {label}
                </label>
                <input
                  type="text"
                  name={key}
                  value={employee[key]}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                  placeholder={key === 'NAME' ? 'Enter full name' : 'Enter expiration date'}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-5 flex-wrap gap-3">
            <button
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-sm font-semibold text-slate-300 hover:text-white transition"
              onClick={() => navigate('/credentials', { state: listState })}
            >
              Cancel
            </button>

            <button
              className="group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
              onClick={handleAdd}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <UserPlus className="w-4 h-4 relative" />
              <span className="relative">Add Employee</span>
            </button>
          </div>
        </section>

        <div className="h-6" />
      </div>
    </div>
  );
};

export default AddEmployeePage;
